import { useCallback, useEffect, useRef, useState } from 'react'
import * as pdfjsLib from 'pdfjs-dist'
import { loadPdfDocument } from '../../common/pdfWorker'

export const MAX_PAGE_WIDTH = 880

/** Fetches a PDF through an authenticated backend route (ArrayBuffer works best on iOS). */
export async function fetchPdfDocument(path: string, token: string | null) {
  const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:2001'
  const response = await fetch(`${backendUrl}${path}`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  })
  if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.status}`)
  const data = await response.arrayBuffer()
  return loadPdfDocument({ data, verbosity: 0 }).promise
}

const SAFE_LINK = /^(https?:|mailto:)/i

/**
 * A drawn page is only a picture, so lay the PDF's web links over it (a video's
 * play button, a form link). Positions are in percent, so they follow any redraw.
 */
async function pageLinks(page: pdfjsLib.PDFPageProxy, base: pdfjsLib.PageViewport) {
  let annotations: Array<{ subtype?: string; url?: string; rect?: number[] }> = []
  try {
    annotations = await page.getAnnotations()
  } catch {
    return [] // The page still reads fine without its links.
  }
  return annotations.flatMap((annotation) => {
    if (annotation.subtype !== 'Link' || !annotation.url || !annotation.rect) return []
    if (!SAFE_LINK.test(annotation.url)) return []
    const [ax, ay, bx, by] = annotation.rect
    const [x1, y1] = base.convertToViewportPoint(ax, ay)
    const [x2, y2] = base.convertToViewportPoint(bx, by)
    const link = document.createElement('a')
    link.href = annotation.url
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    link.title = annotation.url
    link.setAttribute('aria-label', `Open link: ${annotation.url}`)
    link.className =
      'absolute rounded-sm hover:bg-blue-500/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500'
    Object.assign(link.style, {
      left: `${(Math.min(x1, x2) / base.width) * 100}%`,
      top: `${(Math.min(y1, y2) / base.height) * 100}%`,
      width: `${(Math.abs(x2 - x1) / base.width) * 100}%`,
      height: `${(Math.abs(y2 - y1) / base.height) * 100}%`,
    })
    return [link]
  })
}

/**
 * Draws a PDF's pages into `canvasContainerRef` and tracks how far the reader has
 * scrolled `scrollRef`. Reaching the end only counts once every page is on screen.
 */
export function usePdfPages(pdfDoc: pdfjsLib.PDFDocumentProxy | null) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const canvasContainerRef = useRef<HTMLDivElement>(null)
  const renderToken = useRef(0)
  const renderedWidth = useRef(0)
  const pagesReady = useRef(false)
  const [reachedEnd, setReachedEnd] = useState(false)
  const [progress, setProgress] = useState(0)

  const onScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el || !pagesReady.current) return
    const max = el.scrollHeight - el.clientHeight
    const now = max > 0 ? Math.min(100, Math.round((el.scrollTop / max) * 100)) : 100
    // Progress is how far you've read, so scrolling back up never lowers it.
    setProgress((furthest) => Math.max(furthest, now))
    // Within 24px of the bottom counts as reaching the end.
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 24) setReachedEnd(true)
  }, [])

  /** For content that isn't drawn from a PDF: it's on screen, so start counting. */
  const markReady = useCallback(() => {
    pagesReady.current = true
    // If the whole document fits without scrolling, count it as read immediately.
    requestAnimationFrame(onScroll)
  }, [onScroll])

  const renderAllPages = useCallback(
    async (pdf: pdfjsLib.PDFDocumentProxy) => {
      const container = canvasContainerRef.current
      if (!container) return
      const token = ++renderToken.current
      const width = Math.min(container.clientWidth || MAX_PAGE_WIDTH, MAX_PAGE_WIDTH)
      renderedWidth.current = width
      // Draw at screen resolution so text stays sharp on high-density displays.
      const ratio = Math.min(window.devicePixelRatio || 1, 2)
      // The first render shows pages as they are drawn; a resize swaps them in at once.
      const firstRender = container.childElementCount === 0
      const scroller = scrollRef.current
      const sheets: HTMLDivElement[] = []

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum)
        if (token !== renderToken.current) return
        const base = page.getViewport({ scale: 1, rotation: page.rotate })
        const viewport = page.getViewport({
          scale: (width / base.width) * ratio,
          rotation: page.rotate,
        })
        const canvas = document.createElement('canvas')
        canvas.width = viewport.width
        canvas.height = viewport.height
        canvas.setAttribute('role', 'img')
        canvas.setAttribute('aria-label', `Page ${pageNum} of ${pdf.numPages}`)
        canvas.className = 'block h-auto w-full rounded-lg bg-white shadow-sm ring-1 ring-slate-200'
        const sheet = document.createElement('div')
        sheet.className = 'relative'
        sheet.appendChild(canvas)
        sheet.append(...(await pageLinks(page, base)))
        if (token !== renderToken.current) return
        if (firstRender) container.appendChild(sheet)
        else sheets.push(sheet)
        await page.render({ canvas, viewport }).promise
        if (token !== renderToken.current) return
      }

      if (!firstRender) {
        // Rendering can take time for scanned PDFs. Preserve where the person
        // is reading now, including scrolling that happened during the redraw.
        const scrollShare =
          scroller && scroller.scrollHeight > scroller.clientHeight
            ? scroller.scrollTop / (scroller.scrollHeight - scroller.clientHeight)
            : 0
        container.replaceChildren(...sheets)
        if (scroller)
          scroller.scrollTop = scrollShare * (scroller.scrollHeight - scroller.clientHeight)
      }
      markReady()
    },
    [markReady]
  )

  // The canvas container mounts with the dialog, so draw as soon as the PDF is parsed.
  useEffect(() => {
    if (pdfDoc) void renderAllPages(pdfDoc)
  }, [pdfDoc, renderAllPages])

  // Canvases are drawn at a fixed width, so redraw them when the reading column resizes
  // (fullscreen, window resize).
  useEffect(() => {
    const container = canvasContainerRef.current
    if (!pdfDoc || !container) return
    let timer: number | undefined
    const observer = new ResizeObserver(() => {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        const width = Math.min(container.clientWidth, MAX_PAGE_WIDTH)
        if (pagesReady.current && Math.abs(width - renderedWidth.current) > 24)
          void renderAllPages(pdfDoc)
      }, 200)
    })
    observer.observe(container)
    return () => {
      observer.disconnect()
      window.clearTimeout(timer)
    }
  }, [pdfDoc, renderAllPages])

  // Stop drawing into a closed reader.
  useEffect(() => () => void renderToken.current++, [])

  return { scrollRef, canvasContainerRef, onScroll, markReady, progress, reachedEnd }
}

export type PdfPages = ReturnType<typeof usePdfPages>
